// import node_modules
import * as PropTypes from 'prop-types';
import * as React from 'react';
import { Heading, LinkButton, Spacer, Text } from '@storylinerlabs/design';
import { useTranslation } from 'react-i18next';

// import component
import BlogLayout from '../layouts/BlogLayout';
import Column from '../atoms/Column';
import LinkComponent from '../atoms/LinkComponent';
import NewsletterSection from '../elements/NewsletterSection';
import PostsGrid from '../grids/PostsGrid';
import SEO from '../meta/SEO';
import Section from '../atoms/Section';

// define page
const BlogTemplate: React.FunctionComponent<{
  blogPosts: { image: string; slug: string; tags: string[]; title: string }[];
  blogTags: { slug: string; title: string }[];
  hrefLang: string;
}> = ({ blogTags, blogPosts, hrefLang }) => {
  // init hooks
  const { t } = useTranslation('blog');

  // init vars
  const totalPosts = blogPosts.length || 20;

  // init render
  return (
    <>
      <SEO
        description={t('seo.description')}
        hrefLang={hrefLang}
        image={t('seo.image')}
        title={t('seo.title')}
      />

      <BlogLayout blogTags={blogTags} hrefLang={hrefLang}>
        {/* Hero  */}
        <Section>
          {/* Navbar */}
          <Spacer height="64px" />
          <Spacer mobileHeight="25px" tabletHeight="50px" height="75px" />
        </Section>
        <Section>
          <Column cols="ml">
            <Heading alignment="center" tag="h1" isHyphenated={false}>
              {t('hero.title')}
            </Heading>
            <Text alignment="center" size="large">
              {t('hero.intro')}
            </Text>
          </Column>
        </Section>
        <Section>
          <Spacer mobileHeight="25px" tabletHeight="25px" height="25px" />
        </Section>

        {/* Blog  */}
        <PostsGrid bgColor="white" blogPosts={blogPosts} />

        {/* implement pagination */}
        {totalPosts > 20 && (
          <Section>
            <Column>
              <div className="button-container">
                <LinkComponent href={t('nextPage.href')}>
                  <LinkButton
                    color="naked"
                    size="large"
                    text={t('nextPage.label')}
                    iconRight="ri-arrow-right-line"
                    textIsPrimary
                  />
                </LinkComponent>
              </div>
            </Column>
          </Section>
        )}
        <Section>
          <Spacer mobileHeight="50px" tabletHeight="50px" height="50px" />
        </Section>

        {/* Call to action  */}
        <Section bgColor="desertStorm">
          <Spacer height="100px" />
        </Section>
        <NewsletterSection bgColor="desertStorm" />
        <Section bgColor="desertStorm">
          <Spacer height="100px" />
        </Section>
        <Section>
          <Spacer height="50px" />
        </Section>
      </BlogLayout>
    </>
  );
};

BlogTemplate.propTypes = {
  blogPosts: PropTypes.arrayOf(
    PropTypes.shape({
      image: PropTypes.string.isRequired,
      slug: PropTypes.string.isRequired,
      tags: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
      title: PropTypes.string.isRequired,
    }).isRequired,
  ).isRequired,
  blogTags: PropTypes.arrayOf(
    PropTypes.shape({
      slug: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
    }).isRequired,
  ).isRequired,
  hrefLang: PropTypes.string.isRequired,
};

export default BlogTemplate;
